import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { customerService } from '../services/customer-service';
import {
  Customer,
  CustomerContact,
  CreateCustomerData,
  UpdateCustomerData,
  CreateContactData,
  UpdateContactData,
  CustomerSegment,
  UnifiedCustomer,
} from '../types/customer.types';

interface CustomerState {
  customers: Customer[];
  selectedCustomer: Customer | null;
  isLoading: boolean;
  error: string | null;

  fetchCustomers: (search?: string, segment?: CustomerSegment) => Promise<void>;
  fetchCustomerById: (id: string) => Promise<Customer>;
  createCustomer: (data: CreateCustomerData) => Promise<Customer>;
  updateCustomer: (id: string, data: UpdateCustomerData) => Promise<Customer>;
  deleteCustomer: (id: string) => Promise<void>;
  addContact: (customerId: string, data: CreateContactData) => Promise<CustomerContact>;
  updateContact: (
    customerId: string,
    contactId: string,
    data: UpdateContactData
  ) => Promise<CustomerContact>;
  deleteContact: (customerId: string, contactId: string) => Promise<void>;
  clearError: () => void;

  // Lista unificada (Customers + HubspotCompanies)
  unifiedCustomers: UnifiedCustomer[];
  unifiedTotal: number;
  unifiedPage: number;
  unifiedPageSize: number;
  sourceFilter: 'all' | 'prixia' | 'hubspot';
  unifiedSearch: string;
  unifiedLoading: boolean;

  fetchUnified: () => Promise<void>;
  setSourceFilter: (s: 'all' | 'prixia' | 'hubspot') => void;
  setUnifiedSearch: (s: string) => void;
  setUnifiedPage: (p: number) => void;
}

export const useCustomerStore = create<CustomerState>()(
  devtools(
    (set, get) => ({
      customers: [],
      selectedCustomer: null,
      isLoading: false,
      error: null,

      fetchCustomers: async (search?, segment?) => {
        try {
          set({ isLoading: true, error: null });
          const customers = await customerService.getCustomers(search, segment);
          set({ customers, isLoading: false });
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al cargar clientes',
            isLoading: false,
          });
          throw error;
        }
      },

      fetchCustomerById: async (id) => {
        try {
          set({ isLoading: true, error: null });
          const customer = await customerService.getCustomerById(id);
          set({ selectedCustomer: customer, isLoading: false });
          return customer;
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al cargar cliente',
            isLoading: false,
          });
          throw error;
        }
      },

      createCustomer: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const customer = await customerService.createCustomer(data);
          set((state) => ({
            customers: [...state.customers, customer],
            isLoading: false,
          }));
          return customer;
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al crear cliente',
            isLoading: false,
          });
          throw error;
        }
      },

      updateCustomer: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const customer = await customerService.updateCustomer(id, data);
          set((state) => ({
            customers: state.customers.map((c) => (c.id === id ? customer : c)),
            selectedCustomer: state.selectedCustomer?.id === id ? customer : state.selectedCustomer,
            isLoading: false,
          }));
          return customer;
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al actualizar cliente',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteCustomer: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await customerService.deleteCustomer(id);
          set((state) => ({
            customers: state.customers.filter((c) => c.id !== id),
            selectedCustomer: state.selectedCustomer?.id === id ? null : state.selectedCustomer,
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al eliminar cliente',
            isLoading: false,
          });
          throw error;
        }
      },

      addContact: async (customerId, data) => {
        try {
          set({ isLoading: true, error: null });
          const contact = await customerService.addContact(customerId, data);
          set((state) => ({
            customers: state.customers.map((c) => {
              if (c.id === customerId) {
                const contacts = data.isPrimary
                  ? c.contacts.map((ct) => ({ ...ct, isPrimary: false }))
                  : [...c.contacts];
                return { ...c, contacts: [...contacts, contact] };
              }
              return c;
            }),
            selectedCustomer:
              state.selectedCustomer?.id === customerId
                ? {
                    ...state.selectedCustomer,
                    contacts: [...state.selectedCustomer.contacts, contact],
                  }
                : state.selectedCustomer,
            isLoading: false,
          }));
          return contact;
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al agregar contacto',
            isLoading: false,
          });
          throw error;
        }
      },

      updateContact: async (customerId, contactId, data) => {
        try {
          set({ isLoading: true, error: null });
          const contact = await customerService.updateContact(customerId, contactId, data);
          const updateContacts = (contacts: CustomerContact[]) => {
            let updated = contacts.map((ct) => (ct.id === contactId ? contact : ct));
            if (data.isPrimary) {
              updated = updated.map((ct) =>
                ct.id === contactId ? ct : { ...ct, isPrimary: false }
              );
            }
            return updated;
          };
          set((state) => ({
            customers: state.customers.map((c) =>
              c.id === customerId ? { ...c, contacts: updateContacts(c.contacts) } : c
            ),
            selectedCustomer:
              state.selectedCustomer?.id === customerId
                ? {
                    ...state.selectedCustomer,
                    contacts: updateContacts(state.selectedCustomer.contacts),
                  }
                : state.selectedCustomer,
            isLoading: false,
          }));
          return contact;
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al actualizar contacto',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteContact: async (customerId, contactId) => {
        try {
          set({ isLoading: true, error: null });
          await customerService.deleteContact(customerId, contactId);
          const removeContact = (contacts: CustomerContact[]) =>
            contacts.filter((ct) => ct.id !== contactId);
          set((state) => ({
            customers: state.customers.map((c) =>
              c.id === customerId ? { ...c, contacts: removeContact(c.contacts) } : c
            ),
            selectedCustomer:
              state.selectedCustomer?.id === customerId
                ? {
                    ...state.selectedCustomer,
                    contacts: removeContact(state.selectedCustomer.contacts),
                  }
                : state.selectedCustomer,
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al eliminar contacto',
            isLoading: false,
          });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      // Lista unificada
      unifiedCustomers: [],
      unifiedTotal: 0,
      unifiedPage: 1,
      unifiedPageSize: 50,
      sourceFilter: 'all' as const,
      unifiedSearch: '',
      unifiedLoading: false,

      fetchUnified: async () => {
        set({ unifiedLoading: true, error: null });
        try {
          const { sourceFilter, unifiedSearch, unifiedPage, unifiedPageSize } = get();
          const r = await customerService.listUnified({
            search: unifiedSearch || undefined,
            source: sourceFilter,
            page: unifiedPage,
            pageSize: unifiedPageSize,
          });
          set({
            unifiedCustomers: r.data,
            unifiedTotal: r.pagination.total,
            unifiedLoading: false,
          });
        } catch (err: any) {
          set({ error: err?.message ?? 'Error', unifiedLoading: false });
        }
      },

      setSourceFilter: (s) => {
        set({ sourceFilter: s, unifiedPage: 1 });
        get().fetchUnified();
      },

      setUnifiedSearch: (s) => {
        set({ unifiedSearch: s, unifiedPage: 1 });
        get().fetchUnified();
      },

      setUnifiedPage: (p) => {
        set({ unifiedPage: p });
        get().fetchUnified();
      },
    }),
    { name: 'CustomerStore' }
  )
);

export default useCustomerStore;
